import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

interface Props {
    to: string;
    name: string;
    Icon: any;
    activeTab: string;
    setActiveTab: any;
    isSidebarOpen: boolean;
    secondaryActiveTab: string;
    setSecondaryActiveTab: any;
    navigation?: {
        name: string;
        to: string;
    }[];
}
const NavigationDropdown = ({
    to,
    name,
    Icon,
    activeTab,
    setActiveTab,
    isSidebarOpen,
    secondaryActiveTab,
    setSecondaryActiveTab,
    navigation,
}: Props) => {
    const { t } = useTranslation();
    const location = useLocation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const isSelfServices = name === 'self_services';

    useEffect(() => {
        if (!isSelfServices || !navigation) {
            return;
        }
        if (location.pathname.startsWith('/self-services')) {
            setIsDropdownOpen(true);
        } else {
            setIsDropdownOpen(false);
        }
    }, [isSelfServices, navigation, location.pathname]);

    return (
        <>
            {isSelfServices && navigation ? (
                <div
                    className={cn(
                        'w-full mb-1 flex items-center justify-between rounded-s-lg py-4 ps-2 pe-4 text-inherit hover:text-primery-blue-700 hover:bg-white-blue-50/30',
                    )}
                >
                    <button
                        type="button"
                        className="flex min-w-0 flex-1 flex-row items-center bg-transparent text-start text-inherit"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                        <Icon className={`w-6 h-6 ${isSidebarOpen ? 'me-2' : ''}`} />
                        <span className={`${!isSidebarOpen && 'hidden'} text-start text-nowrap`}>{t(name)}</span>
                    </button>
                    {isSidebarOpen && (
                        <Button
                            type="button"
                            className="flex items-center justify-center bg-inherit border-none shadow-none text-inherit hover:text-primery-blue-500 hover:bg-white-blue-300 transition-none"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                            <ArrowDownIcon
                                className={cn('transition-transform', {
                                    hidden: !isSidebarOpen,
                                    'rotate-180': isDropdownOpen,
                                })}
                            />
                        </Button>
                    )}
                </div>
            ) : (
                <NavLink
                    to={to}
                    end={Boolean(navigation)}
                    onClick={() => {
                        setActiveTab(t(name));
                        setSecondaryActiveTab('');
                    }}
                    className={cn(
                        'w-full mb-1 flex items-center justify-between hover:text-primery-blue-700 rounded-s-lg py-4 ps-2 pe-4',
                        {
                            'bg-white-blue-50 text-primery-blue-500': activeTab === t(name),
                            'hover:bg-white-blue-50': activeTab !== t(name),
                            '': isSidebarOpen,
                        },
                    )}
                >
                    <div className="flex flex-row items-center">
                        <Icon className={`w-6 h-6 ${isSidebarOpen ? 'me-2' : ''}`} />
                        <span className={`${!isSidebarOpen && 'hidden'} text-start text-nowrap`}>{t(name)}</span>
                    </div>
                    {navigation && isSidebarOpen && (
                        <Button
                            type="button"
                            className="flex items-center justify-center bg-inherit border-none shadow-none text-inherit hover:text-primery-blue-500 hover:bg-white-blue-300 transition-none"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsDropdownOpen(!isDropdownOpen);
                            }}
                        >
                            <ArrowDownIcon
                                className={cn('transition-transform', {
                                    hidden: !isSidebarOpen,
                                    'rotate-180': isDropdownOpen,
                                })}
                            />
                        </Button>
                    )}
                </NavLink>
            )}
            {isDropdownOpen && isSidebarOpen && (
                <div className="flex flex-col">
                    {navigation?.map((item) => (
                        <NavLink
                            to={item.to}
                            key={t(item.name)}
                            onClick={() => setSecondaryActiveTab(t(item.name))}
                            className={cn(
                                'w-full mb-1 flex items-center justify-between hover:text-primery-blue-700 rounded-s-lg py-4 ps-2 pe-4',
                                {
                                    'bg-primery-blue-900 text-white-blue-50 hover:bg-white-blue-300': isSelfServices
                                        ? location.pathname === item.to ||
                                          location.pathname.startsWith(`${item.to}/`)
                                        : secondaryActiveTab === t(item.name) && activeTab === t(name),
                                    'hover:bg-white-blue-300': isSelfServices
                                        ? !(
                                              location.pathname === item.to ||
                                              location.pathname.startsWith(`${item.to}/`)
                                          )
                                        : secondaryActiveTab !== t(item.name),
                                    '': isSidebarOpen,
                                },
                            )}
                        >
                            <div className="flex flex-row items-center">
                                <span className={cn('ms-6 text-start', { hidden: !isSidebarOpen })}>
                                    {t(item.name)}
                                </span>
                            </div>
                        </NavLink>
                    ))}
                </div>
            )}
        </>
    );
};

export default NavigationDropdown;
